import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PublishErrorAction } from '@unipostudio/shared-types';
import { platformProfileUrl } from '@unipostudio/platform-constants';
import { useDuplicatePost, useRetryPost } from '@/shared/hooks/usePosts';
import { useChannelOAuth } from '@/shared/hooks/useChannelOAuth';
import { useToast } from '@/shared/components/Toast';
import type { PlatformId } from '@/shared/components/PlatformIcon';
import { getApiErrorMessage } from '@/shared/lib/api-errors';
import { ROUTES } from '@/shared/constants/routes';
import { SUPPORT_EMAIL } from '@/shared/constants/legal';

export interface FailureTarget {
  postId: string;
  /** As the API names it: INSTAGRAM, GOOGLE_BUSINESS. */
  platform: string;
  /** The connected account's stored name, for a profile link. */
  username?: string | null;
}

/** Channels connected by a form (token, bot) rather than an OAuth popup: reconnected on the Channels page. */
const MANUAL_CONNECT: ReadonlySet<string> = new Set(['TELEGRAM', 'WHATSAPP']);

/** The link an action opens outside the app, when it is a link rather than a step. */
export function failureActionHref(action: PublishErrorAction, target: FailureTarget): string | null {
  if (action === 'check_on_platform') return platformProfileUrl(target.platform, target.username);
  if (action === 'contact_support') {
    const subject = encodeURIComponent(`Post ${target.postId} on ${target.platform}`);
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}`;
  }
  return null;
}

/**
 * Runs the one action a failed publish offers (PUBLISH_ERROR_COPY). A failed
 * post cannot be edited, so "Edit post" and "Replace media" open a draft
 * copy of it in the composer; the failed post stays where it is.
 */
export function usePublishFailureAction(): { run: (action: PublishErrorAction, target: FailureTarget) => Promise<void>; busy: boolean } {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const retryPost = useRetryPost();
  const duplicatePost = useDuplicatePost();
  const oauth = useChannelOAuth();
  const [busy, setBusy] = useState(false);

  const run = async (action: PublishErrorAction, target: FailureTarget): Promise<void> => {
    switch (action) {
      case 'retry':
      case 'wait':
        setBusy(true);
        try {
          await retryPost.mutateAsync(target.postId);
          toast('success', t('attention.retried'));
        } catch (err) {
          toast('error', getApiErrorMessage(err, t('publishError.toast.retryFailed')));
        } finally {
          setBusy(false);
        }
        return;
      case 'reconnect':
        if (MANUAL_CONNECT.has(target.platform.toUpperCase())) {
          void navigate(ROUTES.CHANNELS);
          return;
        }
        setBusy(true);
        try {
          await oauth.connect(target.platform.toLowerCase() as PlatformId);
        } finally {
          setBusy(false);
        }
        return;
      case 'edit_post':
      case 'replace_media':
        setBusy(true);
        try {
          const copy = await duplicatePost.mutateAsync(target.postId);
          toast('info', t('publishError.toast.copied'));
          void navigate(`${ROUTES.COMPOSER}/${copy.id}`);
        } catch (err) {
          toast('error', getApiErrorMessage(err, t('publishError.toast.copyFailed')));
        } finally {
          setBusy(false);
        }
        return;
      case 'buy_x_credits':
      case 'choose_plan':
        void navigate(`${ROUTES.SETTINGS}?tab=subscription`);
        return;
      case 'check_on_platform':
      case 'contact_support': {
        const href = failureActionHref(action, target);
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
    }
  };

  return { run, busy };
}
